import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import { optimizeImageUrl, getResponsiveImagePreset } from '../../utils/ImageUtils';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './EpisodeCarousel.css';

/**
 * EpisodeCarousel - Reusable episode carousel component
 *
 * Extracted from SeriesPDP to be used across watch pages, modals, and other series interfaces.
 * Provides a horizontal scrolling carousel of episodes with thumbnails and play buttons.
 */
const EpisodeCarousel = ({
  episodes = [],
  selectedEpisode = null,
  onEpisodeSelect,
  className = '',
  variant = 'default',
  showPlayButtons = true,
  showDescriptions = true,
  layout = 'carousel',
  autoPlay = false,
  slidesToShow = 4,
  responsive = true,
  loading = false,
  emptyMessage = 'No episodes available',
  ...props
}) => {
  // Memoized slider settings for performance
  const sliderSettings = useMemo(() => {
    const baseSettings = {
      dots: false,
      infinite: false,
      speed: 500,
      slidesToShow: slidesToShow,
      slidesToScroll: 1,
      swipeToSlide: true,
      focusOnSelect: false,
      accessibility: true,
      arrows: true,
      lazyLoad: 'ondemand',
    };

    if (responsive) {
      baseSettings.responsive = [
        {
          breakpoint: 1024,
          settings: {
            slidesToShow: Math.min(3, slidesToShow),
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: Math.min(2, slidesToShow),
            slidesToScroll: 1,
          },
        },
        {
          breakpoint: 480,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
          },
        },
      ];
    }

    return baseSettings;
  }, [slidesToShow, responsive]);

  // Get optimized thumbnail URL
  const getEpisodeThumbnail = episode => {
    const thumbnailUrl = episode.thumb?.url || episode.thumbnail || episode.poster;
    if (!thumbnailUrl) return null;

    return optimizeImageUrl(thumbnailUrl, getResponsiveImagePreset('EPISODE_THUMBNAIL'));
  };

  // Get episode title with fallback
  const getEpisodeTitle = episode => {
    return episode.title || episode.name || `Episode ${episode.episode_number || '?'}`;
  };

  // Get episode description with fallback
  const getEpisodeDescription = episode => {
    return episode.description || episode.summary || 'No description available';
  };

  // Handle episode selection
  const handleEpisodeClick = episode => {
    if (onEpisodeSelect) {
      onEpisodeSelect(episode);
    }
  };

  // Handle play button click
  const handlePlayClick = (episode, event) => {
    event.stopPropagation(); // Prevent episode selection
    if (onEpisodeSelect) {
      onEpisodeSelect(episode);
    }
  };

  // Check if episode is selected
  const isEpisodeSelected = episode => {
    return selectedEpisode && selectedEpisode._id === episode._id;
  };

  const baseClasses = 'episode-carousel';
  const variantClasses = {
    default: 'episode-carousel-default',
    compact: 'episode-carousel-compact',
    watch: 'episode-carousel-watch',
    modal: 'episode-carousel-modal',
  };

  const containerClasses = [
    baseClasses,
    variantClasses[variant] || variantClasses.default,
    layout === 'grid' ? 'episode-carousel-grid' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  // Loading state
  if (loading) {
    return (
      <div className={`${containerClasses} episode-carousel-loading`}>
        <div className="episode-carousel-skeleton">
          {Array.from({ length: slidesToShow }).map((_, index) => (
            <div key={index} className="episode-skeleton-item">
              <div className="episode-skeleton-thumbnail"></div>
              <div className="episode-skeleton-title"></div>
              <div className="episode-skeleton-description"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Empty state
  if (episodes.length === 0) {
    return (
      <div className={`${containerClasses} episode-carousel-empty`}>
        <div className="episode-empty-content">
          <svg
            className="episode-empty-icon"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2m-9 0h10m-10 0a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2"
            />
          </svg>
          <p className="episode-empty-message">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  // Grid layout
  if (layout === 'grid') {
    return (
      <div className={containerClasses} {...props}>
        <div className="episode-grid">
          {episodes.map(episode => (
            <EpisodeCard
              key={episode._id}
              episode={episode}
              isSelected={isEpisodeSelected(episode)}
              onEpisodeClick={handleEpisodeClick}
              onPlayClick={handlePlayClick}
              showPlayButton={showPlayButtons}
              showDescription={showDescriptions}
              getThumbnail={getEpisodeThumbnail}
              getTitle={getEpisodeTitle}
              getDescription={getEpisodeDescription}
            />
          ))}
        </div>
      </div>
    );
  }

  // Carousel layout
  return (
    <div className={containerClasses} {...props}>
      <div className="episode-carousel-container">
        <Slider {...sliderSettings}>
          {episodes.map(episode => (
            <div key={episode._id} className="episode-slide">
              <EpisodeCard
                episode={episode}
                isSelected={isEpisodeSelected(episode)}
                onEpisodeClick={handleEpisodeClick}
                onPlayClick={handlePlayClick}
                showPlayButton={showPlayButtons}
                showDescription={showDescriptions}
                getThumbnail={getEpisodeThumbnail}
                getTitle={getEpisodeTitle}
                getDescription={getEpisodeDescription}
              />
            </div>
          ))}
        </Slider>
      </div>
    </div>
  );
};

// Episode Card Component
const EpisodeCard = ({
  episode,
  isSelected,
  onEpisodeClick,
  onPlayClick,
  showPlayButton,
  showDescription,
  getThumbnail,
  getTitle,
  getDescription,
}) => {
  const thumbnail = getThumbnail(episode);
  const title = getTitle(episode);
  const description = getDescription(episode);

  return (
    <div
      className={`episode-card ${isSelected ? 'episode-card-selected' : ''}`}
      onClick={() => onEpisodeClick(episode)}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEpisodeClick(episode);
        }
      }}
      aria-label={`Play ${title}`}
    >
      <div className="episode-thumbnail-container">
        {thumbnail ? (
          <img src={thumbnail} alt={title} className="episode-thumbnail" loading="lazy" />
        ) : (
          <div className="episode-thumbnail-placeholder">
            <svg
              className="episode-placeholder-icon"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
        )}

        {showPlayButton && (
          <div className="episode-play-overlay">
            <button
              className="episode-play-button"
              onClick={e => onPlayClick(episode, e)}
              aria-label={`Play ${title}`}
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="episode-play-icon"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}
      </div>

      <div className="episode-content">
        <h3 className="episode-title">{title}</h3>
        {showDescription && <p className="episode-description">{description}</p>}
      </div>
    </div>
  );
};

EpisodeCarousel.propTypes = {
  /** Array of episode objects */
  episodes: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      title: PropTypes.string,
      description: PropTypes.string,
      thumb: PropTypes.object,
      thumbnail: PropTypes.string,
      poster: PropTypes.string,
      episode_number: PropTypes.number,
    }),
  ),

  /** Currently selected episode object */
  selectedEpisode: PropTypes.shape({
    _id: PropTypes.string.isRequired,
  }),

  /** Callback function when episode is selected */
  onEpisodeSelect: PropTypes.func.isRequired,

  /** Additional CSS classes */
  className: PropTypes.string,

  /** Visual variant of the carousel */
  variant: PropTypes.oneOf(['default', 'compact', 'watch', 'modal']),

  /** Whether to show play buttons on episodes */
  showPlayButtons: PropTypes.bool,

  /** Whether to show episode descriptions */
  showDescriptions: PropTypes.bool,

  /** Layout type */
  layout: PropTypes.oneOf(['carousel', 'grid']),

  /** Auto-play carousel */
  autoPlay: PropTypes.bool,

  /** Number of slides to show */
  slidesToShow: PropTypes.number,

  /** Enable responsive breakpoints */
  responsive: PropTypes.bool,

  /** Loading state */
  loading: PropTypes.bool,

  /** Empty state message */
  emptyMessage: PropTypes.string,
};

export default EpisodeCarousel;
